import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Save, Plus, Trash2, Edit3, RefreshCw, Settings as SettingsIcon, Copy, Check, Link as LinkIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Small card at the top of the admin settings tab: shows the public
 * catalog URL and lets the admin copy it with one tap to share with
 * clients via WhatsApp / SMS / bio.
 */
const CatalogLinkCard = () => {
  const [copied, setCopied] = useState(false);
  const catalogUrl = (typeof window !== "undefined" ? window.location.origin : "") + "/servicios";
  const copyCatalog = async () => {
    try {
      await navigator.clipboard.writeText(catalogUrl);
      setCopied(true);
      toast.success("Link del catálogo copiado");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("No pudimos copiar. Cópialo manualmente.");
    }
  };
  return (
    <div
      data-testid="admin-catalog-link-card"
      className="rounded-2xl border border-[#d4af37]/30 bg-gradient-to-r from-[#d4af37]/[0.06] to-transparent p-4 flex flex-wrap items-center gap-3"
    >
      <div className="w-11 h-11 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center shrink-0">
        <LinkIcon className="w-5 h-5 text-[#d4af37]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm font-semibold">Catálogo público para compartir</div>
        <div className="text-[11px] text-neutral-400 mb-1">
          Comparte este link con clientes para que vean todos tus servicios, precios y métodos de pago (no requiere iniciar sesión).
        </div>
        <div className="text-xs font-mono text-[#d4af37] break-all">{catalogUrl}</div>
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          data-testid="admin-catalog-copy"
          onClick={copyCatalog}
          className={`px-3 py-2 rounded-full text-[11px] uppercase tracking-widest border flex items-center gap-1 ${copied ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10"}`}
        >
          {copied ? <><Check className="w-3.5 h-3.5" /> Copiado</> : <><Copy className="w-3.5 h-3.5" /> Copiar link</>}
        </button>
        <a
          data-testid="admin-catalog-open"
          href="/servicios"
          target="_blank"
          rel="noreferrer"
          className="px-3 py-2 rounded-full text-[11px] uppercase tracking-widest border border-white/10 text-neutral-300 hover:text-white hover:border-[#d4af37]/40 flex items-center gap-1"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Abrir
        </a>
      </div>
    </div>
  );
};

/**
 * Admin Settings panel — manage works pricing, reading defaults, broadcast.
 */
export const AdminSettingsTab = () => {
  const { authHeader } = useAuth();
  const [works, setWorks] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingWork, setEditingWork] = useState(null);
  const [newWork, setNewWork] = useState({ name: "", icon: "✨", price_mxn: "", price_usd: "", duration: "", tagline: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [wr, dr] = await Promise.all([
        axios.get(`${API}/users/reading/works`, { headers: authHeader() }),
        axios.get(`${API}/admin/settings/readings`, { headers: authHeader() }),
      ]);
      setWorks(wr.data?.works || []);
      setDefaults(dr.data || {});
    } catch (e) {
      toast.error("Error cargando configuración");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveDefaults = async () => {
    setSaving(true);
    try {
      const payload = {};
      ["normal_price_mxn", "normal_price_usd", "complete_price_mxn", "complete_price_usd",
        "normal_duration_min", "complete_duration_min"].forEach((k) => {
        if (defaults[k] !== undefined && defaults[k] !== "") payload[k] = Number(defaults[k]);
      });
      if (defaults.default_draws_normal) payload.default_draws_normal = defaults.default_draws_normal;
      if (defaults.default_draws_complete) payload.default_draws_complete = defaults.default_draws_complete;
      await axios.put(`${API}/admin/settings/readings`, payload, { headers: authHeader() });
      toast.success("Configuración de lecturas guardada");
    } catch { toast.error("Error guardando"); }
    finally { setSaving(false); }
  };

  const saveWorkOverride = async (w) => {
    try {
      const payload = {
        price_mxn: Number(w.price_mxn), price_usd: Number(w.price_usd),
        name: w.name, tagline: w.tagline, duration: w.duration,
      };
      await axios.patch(`${API}/admin/works/${w.id}`, payload, { headers: authHeader() });
      toast.success("Trabajo actualizado");
      setEditingWork(null);
      load();
    } catch { toast.error("Error"); }
  };

  const createWork = async () => {
    if (!newWork.name || !newWork.price_mxn) return toast.error("Nombre y precio MXN requeridos");
    try {
      await axios.post(`${API}/admin/works`, {
        name: newWork.name, icon: newWork.icon || "✨",
        price_mxn: Number(newWork.price_mxn),
        price_usd: Number(newWork.price_usd) || 0,
        duration: newWork.duration, tagline: newWork.tagline,
      }, { headers: authHeader() });
      toast.success("Trabajo creado");
      setNewWork({ name: "", icon: "✨", price_mxn: "", price_usd: "", duration: "", tagline: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const deleteWork = async (id) => {
    if (!window.confirm("¿Eliminar/ocultar este trabajo del catálogo?")) return;
    try {
      await axios.delete(`${API}/admin/works/${id}`, { headers: authHeader() });
      toast.success("Eliminado");
      load();
    } catch { toast.error("Error"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#d4af37] animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-settings-tab">
      <CatalogLinkCard />
      {/* Reading defaults */}
      <div className="glass-dark rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-xl text-white flex items-center gap-2"><SettingsIcon className="w-4 h-4 text-[#d4af37]" /> Defaults de Lecturas Online</h3>
          <button data-testid="settings-reload" onClick={load} className="text-xs text-neutral-300 hover:text-[#d4af37] flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Recargar</button>
        </div>
        <p className="text-[11px] text-neutral-400 mb-2">Estos valores se aplican como predeterminados al habilitar nuevas lecturas. Las tiradas son número de cartas por fase.</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-neutral-300 text-xs uppercase tracking-widest">Precio Lectura Normal (MXN)</Label>
            <Input data-testid="set-normal-mxn" type="number" value={defaults.normal_price_mxn ?? ""} onChange={(e) => setDefaults({...defaults, normal_price_mxn: e.target.value})} className="bg-black/40 border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-neutral-300 text-xs uppercase tracking-widest">Precio Lectura Normal (USD)</Label>
            <Input data-testid="set-normal-usd" type="number" value={defaults.normal_price_usd ?? ""} onChange={(e) => setDefaults({...defaults, normal_price_usd: e.target.value})} className="bg-black/40 border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-neutral-300 text-xs uppercase tracking-widest">Precio Lectura Completa (MXN)</Label>
            <Input data-testid="set-complete-mxn" type="number" value={defaults.complete_price_mxn ?? ""} onChange={(e) => setDefaults({...defaults, complete_price_mxn: e.target.value})} className="bg-black/40 border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-neutral-300 text-xs uppercase tracking-widest">Precio Lectura Completa (USD)</Label>
            <Input data-testid="set-complete-usd" type="number" value={defaults.complete_price_usd ?? ""} onChange={(e) => setDefaults({...defaults, complete_price_usd: e.target.value})} className="bg-black/40 border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-neutral-300 text-xs uppercase tracking-widest">Duración Normal (min)</Label>
            <Input data-testid="set-normal-dur" type="number" value={defaults.normal_duration_min ?? ""} onChange={(e) => setDefaults({...defaults, normal_duration_min: e.target.value})} className="bg-black/40 border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-neutral-300 text-xs uppercase tracking-widest">Duración Completa (min)</Label>
            <Input data-testid="set-complete-dur" type="number" value={defaults.complete_duration_min ?? ""} onChange={(e) => setDefaults({...defaults, complete_duration_min: e.target.value})} className="bg-black/40 border-white/10 text-white" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1">Tiradas Normal (JSON por fase)</Label>
            <Textarea data-testid="set-draws-normal" rows={2} value={JSON.stringify(defaults.default_draws_normal || {}, null, 0)} onChange={(e) => {
              try { setDefaults({...defaults, default_draws_normal: JSON.parse(e.target.value || "{}")}); } catch { /* ignore */ }
            }} className="bg-black/40 border-white/10 text-white text-xs font-mono" placeholder='{"española":2,"tarot":1}' />
          </div>
          <div>
            <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1">Tiradas Completa (JSON por fase)</Label>
            <Textarea data-testid="set-draws-complete" rows={2} value={JSON.stringify(defaults.default_draws_complete || {}, null, 0)} onChange={(e) => {
              try { setDefaults({...defaults, default_draws_complete: JSON.parse(e.target.value || "{}")}); } catch { /* ignore */ }
            }} className="bg-black/40 border-white/10 text-white text-xs font-mono" placeholder='{"española":3,"tarot":2,"runas":1}' />
          </div>
        </div>
        <button data-testid="save-reading-defaults" onClick={saveDefaults} disabled={saving} className="btn-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2"><Save className="w-3.5 h-3.5" /> Guardar Defaults</button>
      </div>

      {/* Works catalog */}
      <div className="glass-dark rounded-2xl p-5 space-y-3">
        <h3 className="font-serif text-xl text-white mb-2">Catálogo de Trabajos (precios y disponibilidad)</h3>
        <p className="text-[11px] text-neutral-400 mb-3">Edita precios de Magia Suprema/Poder Máximo o crea trabajos personalizados que aparecerán al final de cada Lectura Online.</p>
        <div className="space-y-2">
          {works.map((w) => (
            <div key={w.id} className="bg-black/30 border border-white/10 rounded-xl p-3" data-testid={`work-row-${w.id}`}>
              {editingWork?.id === w.id ? (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-2 items-center">
                  <Input value={editingWork.name} onChange={(e) => setEditingWork({...editingWork, name: e.target.value})} placeholder="Nombre" className="bg-black/40 border-white/10 text-white text-xs" />
                  <Input type="number" value={editingWork.price_mxn} onChange={(e) => setEditingWork({...editingWork, price_mxn: e.target.value})} placeholder="MXN" className="bg-black/40 border-white/10 text-white text-xs" />
                  <Input type="number" value={editingWork.price_usd} onChange={(e) => setEditingWork({...editingWork, price_usd: e.target.value})} placeholder="USD" className="bg-black/40 border-white/10 text-white text-xs" />
                  <Input value={editingWork.duration} onChange={(e) => setEditingWork({...editingWork, duration: e.target.value})} placeholder="Duración" className="bg-black/40 border-white/10 text-white text-xs" />
                  <div className="flex gap-1">
                    <button data-testid={`work-save-${w.id}`} onClick={() => saveWorkOverride(editingWork)} className="text-[11px] px-2.5 py-1 rounded bg-[#d4af37] text-black font-semibold">Guardar</button>
                    <button onClick={() => setEditingWork(null)} className="text-[11px] px-2.5 py-1 rounded border border-white/10 text-neutral-300">Cancelar</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-2xl">{w.icon}</span>
                    <div className="min-w-0">
                      <div className="text-white text-sm font-semibold truncate">{w.name}</div>
                      <div className="text-[10px] text-neutral-400">{w.tagline}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-emerald-300">${Number(w.price_mxn).toLocaleString("es-MX")} MXN</span>
                    <span className="text-amber-300">${Number(w.price_usd).toLocaleString("en-US")} USD</span>
                    <span className="text-neutral-500">{w.duration?.slice(0, 30)}</span>
                    <button data-testid={`work-edit-${w.id}`} onClick={() => setEditingWork({...w, price_mxn: String(w.price_mxn), price_usd: String(w.price_usd)})} className="text-[11px] px-2 py-0.5 rounded border border-white/10 text-neutral-300 hover:text-[#d4af37] inline-flex items-center gap-1"><Edit3 className="w-3 h-3" /> Editar</button>
                    <button data-testid={`work-delete-${w.id}`} onClick={() => deleteWork(w.id)} className="text-[11px] px-2 py-0.5 rounded border border-red-500/30 text-red-300 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Ocultar</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Create new work */}
        <div className="border-t border-white/10 pt-3 mt-4 space-y-2">
          <div className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> Nuevo trabajo personalizado</div>
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-2">
            <Input value={newWork.icon} onChange={(e) => setNewWork({...newWork, icon: e.target.value})} placeholder="Icono (emoji)" className="bg-black/40 border-white/10 text-white text-xs" data-testid="new-work-icon" />
            <Input value={newWork.name} onChange={(e) => setNewWork({...newWork, name: e.target.value})} placeholder="Nombre" className="bg-black/40 border-white/10 text-white text-xs lg:col-span-2" data-testid="new-work-name" />
            <Input type="number" value={newWork.price_mxn} onChange={(e) => setNewWork({...newWork, price_mxn: e.target.value})} placeholder="MXN" className="bg-black/40 border-white/10 text-white text-xs" data-testid="new-work-mxn" />
            <Input type="number" value={newWork.price_usd} onChange={(e) => setNewWork({...newWork, price_usd: e.target.value})} placeholder="USD" className="bg-black/40 border-white/10 text-white text-xs" data-testid="new-work-usd" />
            <Input value={newWork.duration} onChange={(e) => setNewWork({...newWork, duration: e.target.value})} placeholder="Duración" className="bg-black/40 border-white/10 text-white text-xs" data-testid="new-work-dur" />
          </div>
          <Input value={newWork.tagline} onChange={(e) => setNewWork({...newWork, tagline: e.target.value})} placeholder="Tagline corto" className="bg-black/40 border-white/10 text-white text-xs" data-testid="new-work-tagline" />
          <button data-testid="new-work-save" onClick={createWork} className="btn-gold px-3 py-2 rounded-full text-xs uppercase tracking-widest inline-flex items-center gap-2"><Plus className="w-3 h-3" /> Crear trabajo</button>
        </div>
      </div>
    </div>
  );
};


/**
 * Mass-broadcast panel.
 */
export const AdminBroadcastTab = () => {
  const { authHeader } = useAuth();
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const send = async () => {
    if (!body.trim()) return toast.error("Mensaje vacío");
    if (!window.confirm(`¿Enviar este mensaje a TODOS los usuarios?\n\n${body.slice(0, 200)}...`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/broadcast`, { body }, { headers: authHeader() });
      setLastResult(r.data);
      toast.success(`Mensaje enviado a ${r.data.delivered_to} usuarios`);
      setBody("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSending(false); }
  };

  return (
    <div className="glass-dark rounded-2xl p-5 space-y-3" data-testid="admin-broadcast-tab">
      <h3 className="font-serif text-xl text-white flex items-center gap-2">📣 Mensaje Masivo</h3>
      <p className="text-[11px] text-neutral-400">Este mensaje se entregará por separado a cada usuario no-admin como un mensaje privado del administrador. Aparece etiquetado como 📣 broadcast en su chat.</p>
      <Textarea
        data-testid="broadcast-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={8}
        maxLength={4000}
        placeholder="Escribe tu mensaje masivo aquí... (markdown no es procesado)"
        className="bg-black/40 border-white/10 text-white"
      />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[10px] text-neutral-500">{body.length} / 4000 caracteres</div>
        <button data-testid="broadcast-send" onClick={send} disabled={sending || !body.trim()} className="btn-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Enviar a TODOS
        </button>
      </div>
      {lastResult && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-xs text-emerald-300">
          ✓ Último envío: {lastResult.delivered_to} destinatarios.
        </div>
      )}
    </div>
  );
};
